import { Fragment, memo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
 Text,
 Center,
 Flex,
 Stack,
 Box,
 AccordionButton,
 AccordionIcon,
 AccordionItem,
 AccordionPanel,
 Accordion,
} from '@chakra-ui/react';
import { NumericFormat } from 'react-number-format';
import { InputText } from '../Input';
import QuestionCard from './QuestionCard';
import { AttachmentButton } from '../Button';
import EvidenceViewerModal from '../Modals/EvidenceViewerModal';
import MemoizedCheckbox from '../Input/MemoizedCheckbox';
import RadioQuestion from './RadioQuestion';

const MemoizedAccordionItem = memo(({ theme, children, index }) => {
 return (
  <AccordionItem
   key={theme?.id_theme}
   bg='white'
   borderRadius='12px'
   border='none'
   mb='16px'
   data-index={index}
  >
   {({ isExpanded }) => (
    <>
     <AccordionButton
      p='6'
      borderRadius='12px'
      _hover={{
       bg: 'white',
      }}
     >
      <Box
       as='span'
       fontSize={['18px', '20px']}
       fontWeight={700}
       flex='1'
       textAlign='left'
       color={isExpanded ? '#0073BB' : '#5C6066'}
       whiteSpace='nowrap'
       py='3px'
      >
       {index + 1}. {theme?.title}
      </Box>

      <Text as='span' color='#16262E' pr='10px'>
       {theme?.answeredQuestions}/{theme?.totalQuestions}
       <Text as='span' pl='6px' display={['none', 'inline-flex']}>
        perguntas respondidas
       </Text>
      </Text>
      <AccordionIcon />
     </AccordionButton>

     <AccordionPanel p={['4px 0px 8px', '0px 24px 16px']}>
      <Text color='black' mb='6'>
       Marque as opções que melhor descrevem as práticas atualmente em vigor em sua
       empresa.
      </Text>
      {children}
     </AccordionPanel>
    </>
   )}
  </AccordionItem>
 );
});

export default function PillarSurvey({
 themesData,
 themes,
 defaultValues,
 userId,
 surveyId,
 companyId,
}) {
 const [themeIndex, setThemeIndex] = useState([0]);
 const [attachEvidenceIsOpen, setAttachEvidenceIsOpen] = useState(false);
 const [currentQuestionName, setCurrentQuestionName] = useState();
 const [currentQuestion, setCurrentQuestion] = useState();

 const { handleSubmit, register, control } = useForm({
  defaultValues,
  mode: 'onBlur',
 });

 const handleModal = (questionName, question) => {
  setCurrentQuestionName(questionName);
  setCurrentQuestion(question);
  setAttachEvidenceIsOpen(true);
 };

 const closeModal = () => {
  setAttachEvidenceIsOpen(false);
  setCurrentQuestionName();
  setCurrentQuestion();
 };

 const onSubmit = async (data) => {};

 const confirmSendResponses = async () => {};

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <Accordion allowMultiple index={themeIndex} onChange={(e) => setThemeIndex(e)}>
    {themes?.map((theme, index) => (
     <MemoizedAccordionItem index={index} theme={theme} key={theme?.id_theme}>
      {themesData[index]?.levels?.map((level) => (
       <Fragment key={level?.nivel}>
        <Center bg='#EAEEF0' h='41px' minH='40px' mb={['6px', '16px']}>
         <Text color='#5C6066' fontWeight={700}>
          Nível {level?.nivel}
         </Text>
        </Center>

        <Stack spacing='4' mb={['20px', '40px']}>
         {level?.questions?.map((question) => (
          <QuestionCard
           key={question.id_question}
           question={question}
           surveyId={surveyId}
           userId={userId}
           companyId={companyId}
          >
           {question?.type === 'text' && (
            <InputText
             {...register(
              `text_question_${question.id_question}_${theme.id_theme}.value`
             )}
             type='text'
             w='400px'
             expandable
             isDisabled={true}
             isViewMode={true}
            />
           )}

           {question?.type === 'multiple_choice' && (
            <Flex gap='12px' flexWrap='wrap' className='questionary-app'>
             {question.options?.map((option, index) => (
              <MemoizedCheckbox
               index={index}
               key={option.id_question_option_template}
               optionId={`checkbox_question_${question.id_question}_${theme.id_theme}_${option.id_question_option_template}`}
               value={option.description}
               questionId={question.id_question}
               themeId={theme.id_theme}
               register={register}
               isViewMode={true}
              />
             ))}
            </Flex>
           )}

           {question?.type === 'multi_choice' && (
            <RadioQuestion
             control={control}
             question={question}
             themeId={theme.id_theme}
             defaultValues={defaultValues}
             isReadOnly={true}
            />
           )}

           {question?.type === 'number' && (
            <InputText
             {...register(
              `number_question_${question.id_question}_${theme.id_theme}.value`,
              {
               pattern: /\S/,
              }
             )}
             type='number'
             w='400px'
             isDisabled={true}
             isViewMode={true}
            />
           )}
           {question?.type === 'money' && (
            <Flex w='400px'>
             <Controller
              name={`money_question_${question.id_question}_${theme.id_theme}.value`}
              control={control}
              render={({ field: { ref, ...rest } }) => (
               <NumericFormat
                displayType='input'
                thousandSeparator=','
                decimalSeparator='.'
                prefix='R$ '
                decimalScale={3}
                getInputRef={ref}
                min={0}
                customInput={InputText}
                isDisabled={true}
                isViewMode={true}
                {...rest}
               />
              )}
             />
            </Flex>
           )}
           {question?.type === 'percentage' && (
            <Flex w='400px'>
             <Controller
              name={`percentage_question_${question.id_question}_${theme.id_theme}.value`}
              control={control}
              render={({ field: { ref, onChange, ...rest } }) => (
               <NumericFormat
                suffix='%'
                displayType='input'
                maxLength={4}
                fixedDecimalScale
                onValueChange={(values) => {
                 const { value } = values;
                 const numericValue = parseInt(value, 10);
                 if (isNaN(numericValue)) {
                  onChange(value);
                 } else if (numericValue <= 100) {
                  onChange(value);
                 } else {
                  onChange('100');
                 }
                }}
                getInputRef={ref}
                customInput={InputText}
                isDisabled={true}
                isViewMode={true}
                {...rest}
               />
              )}
             />
            </Flex>
           )}
           <AttachmentButton
            control={control}
            question={{ ...question, id_theme: theme?.id_theme }}
            handleModal={handleModal}
           />
          </QuestionCard>
         ))}
        </Stack>
       </Fragment>
      ))}
     </MemoizedAccordionItem>
    ))}
   </Accordion>

   {currentQuestionName && currentQuestion && (
    <EvidenceViewerModal
     isOpen={attachEvidenceIsOpen}
     onClose={closeModal}
     control={control}
     currentQuestionName={currentQuestionName}
    />
   )}
  </form>
 );
}
